import { useEffect, useState } from "react"
import { useAppDispatch } from '../../../app/hooks'
import { Cube20Regular, Bot24Regular } from '@fluentui/react-icons';
import { removeCard } from "../../../Slices/DashboardSlice"
import { GetMachineData, MachineData } from "../../../services/MachineService"
import OeeDial from "../OEE/OeeDial"
import { CardHeader } from "./CardHeader";
import { CollapseTop } from "./CollapseTop";
import PrimaryDial, { DialSideText } from "./PrimaryDial";
import { OneColumn, TwoColumns } from "./Columns";
import { LineChart } from "./LineChart";
import { CollapsableSection } from "./CollapsableSection";
import { Attribute, filterByTimeRange, firstValue, lastValueAsNumber } from "../../../Types/Attribute";
import { usePilotDrawer } from "../../../contexts/PilotDrawerContext";
import { CardProps } from "../CardProps";


export function MachineCard(cardProps: CardProps) {
    const [machineData, setMachineData] = useState({} as MachineData);
    const dispatch = useAppDispatch();
    const { openWithPrompt } = usePilotDrawer();
    const [isProducingExpanded, setIsProducingExpanded] = useState(false);
    const [oeeExpaned, setIsOeeExpanded] = useState(true);
    const [rapsExpanded, setIsRapsExpanded] = useState(false);
    const [moreRapsExpanded, setIsMoreRapsExpanded] = useState(false);
    const [sustainabilityExpanded, setIsSustainabilityExpanded] = useState(false);
    const [facilityExpanded, setIsFacilityExpanded] = useState(false);

    const [oeeTimeRange, setOeeTimeRange] = useState('8 hours average');

    const [oee, setOee] = useState(0);
    const [lastOee, setLastOee] = useState(0);
    const [availability, setAvailability] = useState(0);
    const [performance, setPerformance] = useState(0);
    const [quality, setQuality] = useState(0);


    const oeeTimeRanges = ['8 hours average', '24 hours average', '7 days average'];
    const isProducingTimeRanges = ['8 hours', '24 hours', '7 days'];
    const [index, setIndex] = useState(0);
    const [isProducingIndex, setIsProducingIndex] = useState(0);

    const [roomTemperature, setRoomTemperature] = useState<Attribute>();
    const [roomHumidity, setRoomHumidity] = useState<Attribute>();
    const [roomPressure, setRoomPressure] = useState<Attribute>();
    const [isProducing, setIsProducing] = useState<Attribute>();
    const [isProducingFiltered, setIsProducingFiltered] = useState<Attribute>();

    const [firstRapsEntries, setFirstRapsEntries] = useState<[string, Attribute][]>([]);
    const [secondRapsEntries, setSecondRapsEntries] = useState<[string, Attribute][]>([]);
    const [sustainability, setSustainability] = useState<{ [key: string]: string }>({});


    useEffect(() => {
        (async () => {
            const data = await GetMachineData(cardProps.elementGuid);
            if (data) {
                setMachineData(data);
                setAvailability(lastValueAsNumber(data.availability8h));
                setPerformance(lastValueAsNumber(data.performance8h));
                setQuality(lastValueAsNumber(data.quality8h));
                setOee(lastValueAsNumber(data.oee8h));
                setLastOee(lastValueAsNumber(data.oee));

                const filteredProducing = filterByTimeRange(data.isProducing, 24);
                if (filteredProducing) {
                    setIsProducingFiltered(filteredProducing);
                }


                setFirstRapsEntries(Object.entries(data.rapsInfo).slice(0, 8));
                setSecondRapsEntries(Object.entries(data.rapsInfo).slice(8));

                const newSustainability = {
                    "Energy Consumption (Daily)": `${lastValueAsNumber(data.energyConsumptionDaily).toFixed(3)} ${data.energyConsumptionDaily?.Uom ?? ""}`,
                    "CO2 Equivalents (Daily)": `${lastValueAsNumber(data.co2equivalentsDaily).toFixed(3)} ${data.co2equivalentsDaily?.Uom ?? ""}`,
                    "CO2 Equivalents (Batch)": `${lastValueAsNumber(data.co2equivalentsBatch).toFixed(3)} ${data.co2equivalentsBatch?.Uom ?? ""}`,
                    "CO2 Equivalents (Insert)": `${lastValueAsNumber(data.co2equivalentsInsert).toFixed(3)} ${data.co2equivalentsInsert?.Uom ?? ""}`,
                };

                setSustainability(newSustainability);

                setRoomTemperature(data.roomTemperature);
                setRoomHumidity(data.roomHumidity);
                setRoomPressure(data.roomPressure);
                setIsProducing(data.isProducing);
            }
        })();
    }, [cardProps.elementGuid]);

    useEffect(() => {
        machineData && setOeeTimeRange(oeeTimeRanges[index]);
    }, [machineData]);

    const ToggleTimeRange = () => {
        const nextIndex = (index + 1) % 3;
        setIndex(nextIndex);

        const ranges = [
            {
                availability: lastValueAsNumber(machineData.availability8h),
                performance: lastValueAsNumber(machineData.performance8h),
                quality: lastValueAsNumber(machineData.quality8h),
                oee: lastValueAsNumber(machineData.oee8h)
            },
            {
                availability: lastValueAsNumber(machineData.availability24h),
                performance: lastValueAsNumber(machineData.performance24h),
                quality: lastValueAsNumber(machineData.quality24h),
                oee: lastValueAsNumber(machineData.oee24h)
            },
            {
                availability: lastValueAsNumber(machineData.availability7d),
                performance: lastValueAsNumber(machineData.performance7d),
                quality: lastValueAsNumber(machineData.quality7d),
                oee: lastValueAsNumber(machineData.oee7d)
            },
        ];
        setOeeTimeRange(oeeTimeRanges[nextIndex]);

        const { availability, performance, quality, oee } = ranges[nextIndex];
        setAvailability(availability);
        setPerformance(performance);
        setQuality(quality);
        setOee(oee);
    };


    const ToggleIsProducingTimeRange = () => {
        if (isProducing === undefined) return;
        const nextIndex = (isProducingIndex + 1) % 3;
        setIsProducingIndex(nextIndex);
        let filtered = null;
        switch (isProducingIndex) {
            case 0: {
                filtered = filterByTimeRange(isProducing, 8);
                break;
            }
            case 1: {
                filtered = filterByTimeRange(isProducing, 24);
                break;
            }
            case 2: {
                filtered = filterByTimeRange(isProducing, 168);
                break;
            }
        };
        if (filtered) {
            setIsProducingFiltered(filtered);
        }
    };




    const Dismiss = () => {
        cardProps.onDismiss && cardProps.onDismiss();
    }

    const icon = <Cube20Regular />;

    const expand = (currentValue: boolean, setState: React.Dispatch<React.SetStateAction<boolean>>) => {
        setState(!currentValue);
    };

    const oeeDialSideTexts: DialSideText[] = [
        { name: "Average 8h OEE", value: Math.round(lastValueAsNumber(machineData.oee8h)) },
        { name: "Average 24h OEE", value: Math.round(lastValueAsNumber(machineData.oee24h)) },
        { name: "Average 7d OEE", value: Math.round(lastValueAsNumber(machineData.oee7d)) }
    ]

    const OeePanel = () => {
        return (
            <div className="py-4">
                <PrimaryDial value={oee} threshold={80} midText={oeeTimeRange} texts={oeeDialSideTexts} />
                <div className="flex justify-between -mt-4 flex-1">
                    <OeeDial title="AVAILABILITY" alias={""} attributeGuid="" value={availability} />
                    <OeeDial title="PERFORMANCE" alias="" attributeGuid="" value={performance} />
                    <OeeDial title="QUALITY" alias="" attributeGuid="" value={quality} />
                </div>
            </div>
        );
    };

    const IsProducingDot = () => {
        return (
            <div className="flex justify-center items-center">
                <div className={`h-6 w-6 drop-shadow-md rounded-full ${isProducing && lastValueAsNumber(isProducing) === 1 ? "bg-good-green" : "bg-bad-red"}`}>
                </div>
            </div>
        )
    }

    const ToggleRapsInfo = () => {
        setIsRapsExpanded(!rapsExpanded);
        if (rapsExpanded) {
            setIsMoreRapsExpanded(false);
        }
    }

    const handleAskAssistant = () => {
        const prompt = `Tell me about the machine "${cardProps.name}". Current OEE is ${oee}%, availability is ${availability}%, performance is ${performance}%, and quality is ${quality}%. Is this machine performing well?`;
        openWithPrompt(prompt);
    };


    return (machineData &&
        <div
            className="bg-primary-light bg-primary border-border-light dark:bg-primary-dark dark:border-border-dark border-1 drop-shadow-lg"
            onClick={(e) => e.stopPropagation()}
            data-tour={`card-machine-${cardProps.elementGuid}`}
        >
            <CardHeader title={"Machine View"} subtitle={cardProps.name} onClick={cardProps.onDismiss} icon={icon} />
            {/* Ask Assistant Button */}
            <div className="px-4 pt-2">
                <button
                    onClick={handleAskAssistant}
                    className="w-full hover:bg-collapse-header-light dark:hover:bg-collapse-header-dark text-text-light dark:text-text-dark text-sm py-2 px-3 rounded-md transition-colors duration-200 flex items-center justify-center gap-2"
                >
                    <Bot24Regular /> Ask Assistant about this machine
                </button>
            </div>

            <div className="py-2 space-y-2 pt-5">
                <CollapseTop title="OEE" rightButton={ToggleTimeRange} rightButtonTitle={oeeTimeRange} expand={() => expand(oeeExpaned, setIsOeeExpanded)} rightValueUnderline={true} startsExpanded={true} />
                <CollapsableSection expanded={oeeExpaned} children={<OeePanel />} />

                <CollapseTop title="Is Producing"
                    rightButton={ToggleIsProducingTimeRange}
                    rightButtonTitle={isProducingTimeRanges[isProducingIndex]}
                    expand={() => { expand(isProducingExpanded, setIsProducingExpanded) }}
                    extraElement={<IsProducingDot />}
                    rightValueUnderline={true}
                />
                <CollapsableSection expanded={isProducingExpanded} children={<div className="space-y-2 px-1 pb-8">
                    {isProducing && <LineChart chartData={isProducingFiltered?.Values ?? []} uom={isProducingFiltered?.Uom ?? ''} chartName="" stepped={true} />}
                </div>} />

                {machineData.rapsInfo &&
                    <>
                        <CollapseTop title="Raps info" rightButton={() => { }} rightButtonTitle={'MO: ' + firstValue(machineData.rapsInfo.MO)} expand={() => ToggleRapsInfo()} />
                        <CollapsableSection expanded={rapsExpanded} children={<TwoColumns keyValuePairs={firstRapsEntries} />} />

                        {rapsExpanded &&
                            <>
                                <CollapseTop title="More" rightButton={() => { }} rightButtonTitle={''} expand={() => { setIsMoreRapsExpanded(!moreRapsExpanded) }} />
                                <CollapsableSection expanded={moreRapsExpanded} children={<TwoColumns keyValuePairs={secondRapsEntries} />} />
                            </>
                        }

                        <CollapseTop title="Sustainability" rightButton={() => { }} rightButtonTitle={''} expand={() => setIsSustainabilityExpanded(!sustainabilityExpanded)} />
                        <CollapsableSection expanded={sustainabilityExpanded} children={<OneColumn keyValuePairs={sustainability} />} />

                        <CollapseTop title="Facility data" rightButton={() => { }} rightButtonTitle={''} expand={() => { setIsFacilityExpanded(!facilityExpanded) }} />
                        <CollapsableSection expanded={facilityExpanded} children={<div className="space-y-2 px-1 pb-8">
                            {roomTemperature && <>
                                <LineChart chartData={roomTemperature?.Values} uom={roomTemperature.Uom} chartName="Room temperature" />
                            </>}
                            {roomHumidity && <>
                                <LineChart chartData={roomHumidity?.Values} uom={roomHumidity.Uom} chartName="Room humidity" />
                            </>}
                            {roomPressure && <>
                                <LineChart chartData={roomPressure?.Values} uom={roomPressure.Uom} chartName="Room pressure" />
                            </>}
                        </div>} />
                    </>
                }
            </div>
        </div>

    )
}
